import gql from 'graphql-tag'
import hoistNonReactStatics from 'hoist-non-react-statics'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Subtract } from 'utility-types'

import { UserRoleEnumType } from 'shared/common/global-types.runtime'
import { SessionUser } from 'shared/utils/auth/types.d'
import * as TypeGuards from 'shared/utils/type-guards'

import { useSession } from 'shared/contexts/session-provider/session-provider'
import { OrganizationTypeEnumType } from '~/common/types/enum-types'
import { CurrentUserFragment } from '~/common/types/graphql'
import { setUser } from '~/utils/auth/browser'
import Loader from '~/views/components/ui/Loader'
import { useUpdatedSubscription } from './useUpdatedSubscription'
import { useCurrentUserLazyQuery } from './useCurrentUserLazyQuery'

const LoadingWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
`

export interface CurrentUser extends CurrentUserFragment {
  name: string

  isAdmin(): boolean
  isContributor(): boolean
  isGuide(): boolean
  isManager(): boolean
  isObserver(): boolean
  isSuperAdmin(): boolean
  isTaskOwner(): boolean
}

export interface UserContext {
  currentUser?: CurrentUser | null
  isReady: boolean
}

const CurrentUserContext = React.createContext<UserContext>({
  currentUser: null,
  isReady: false,
})

const useCurrentUserContext = () => {
  const context = React.useContext(CurrentUserContext)

  if (context === null) {
    throw new Error('CurrentUserContext was used outside the provider')
  }

  return context
}

interface CurrentUserProviderProps {
  children: React.ReactNode
  /**
   * Can optionally set the current user. The current user will be fetched if not
   * provided.
   *
   * Note: this is useful for testing so you can mock the current user without needing
   * to wait for an async fetch mock
   */
  user?: CurrentUserFragment
}

const CurrentUserProvider = ({
  children,
  user: defaultUser,
}: CurrentUserProviderProps) => {
  const { active, session } = useSession()
  const activeSession = Boolean(active && session?.accessToken)

  const [loadCurrentUser, { called, data, loading }] = useCurrentUserLazyQuery()

  const currentUser = defaultUser || data?.currentUser

  /**
   * Embeds the current user to global state so it's
   * accessible outside of the React context
   */
  useEffect(() => {
    if (currentUser) {
      /**
       * I'm type casting here on purpose. These fields don't have any
       * business being nullable and we have logical checks that are
       * preventing users without emails and roles. We just need to
       * update our graphql user type fields
       */
      const sessionUser: SessionUser = {
        __typename: 'UserType',
        id: currentUser.id,
        firstName: currentUser.firstName as string,
        lastName: currentUser.lastName as string,
        email: currentUser.email as string,
        role: currentUser.role as UserRoleEnumType,
      }

      if (currentUser.organization) {
        const organization = currentUser.organization
        sessionUser.organization = {
          id: organization.id,
          name: organization.name as string,
          domain: currentUser.organization.domain as string,
          type: currentUser.organization.type as OrganizationTypeEnumType,
        }
      }

      setUser(sessionUser)
    }
  }, [currentUser])

  useUpdatedSubscription(currentUser?.organization?.id)

  useEffect(() => {
    if (!currentUser && !called && activeSession) {
      loadCurrentUser()
    }
  }, [currentUser, activeSession, called])

  const context = !TypeGuards.isUser(currentUser)
    ? null
    : {
        ...currentUser,
        name: [currentUser?.firstName, currentUser?.lastName].join(' '),
        isAdmin: () => TypeGuards.isAdmin(currentUser),
        isSuperAdmin: () => TypeGuards.isSuperAdmin(currentUser),
        isGuide: () => TypeGuards.isGuide(currentUser),
        isContributor: () => TypeGuards.isContributor(currentUser),
        isObserver: () => TypeGuards.isObserver(currentUser),
        isTaskOwner: () => TypeGuards.isTaskOwner(currentUser),
        isManager: () => TypeGuards.isManager(currentUser),
      }

  const sessionReady = Boolean(defaultUser || activeSession)
  const contextReady = Boolean(currentUser && sessionReady && !loading)

  const shouldRenderChildren = !activeSession || contextReady

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser: context,
        isReady: contextReady,
      }}
    >
      {shouldRenderChildren ? (
        children
      ) : (
        <LoadingWrapper>
          <Loader />
        </LoadingWrapper>
      )}
    </CurrentUserContext.Provider>
  )
}

export interface WithCurrentUserProps {
  currentUser: CurrentUser
}

const withCurrentUser = <P extends WithCurrentUserProps>(
  Component: React.ComponentType<P>,
) => {
  const CurrentUserComponent = React.forwardRef<
    any,
    Subtract<P, WithCurrentUserProps>
  >((props, ref) => {
    const { currentUser } = useCurrentUserContext()

    // todo: resolve this from ENG-2946
    // @ts-ignore - can't figure out how to resolve this
    return <Component {...(props as P)} ref={ref} currentUser={currentUser} />
  })

  // todo: resolve this from ENG-2946
  // @ts-ignore - can't figure out how to resolve this
  return hoistNonReactStatics(CurrentUserComponent, Component)
}

export { CurrentUserProvider, useCurrentUserContext, withCurrentUser }

/**
 * Used to generate useCurrentUserContextOrganizationUpdatedSubscription
 */

gql`
  subscription CurrentUserContext_OrganizationUpdated($organizationId: ID!) {
    organizationUpdated(organizationId: $organizationId) {
      id
      currencyCode
      countryCode
    }
  }
`

export default CurrentUserContext

gql`
  query CurrentUserContext_CurrentUser {
    currentUser {
      id
      ...CurrentUser
    }
  }

  fragment CurrentUser on UserType {
    id
    firstName
    lastName
    email
    role

    adminTeamsEnabled
    createdAt
    hasAccount
    onlyNotificationsTeamsEnabled
    projectsCount
    weeklyHours
    avatar {
      originalUrl
    }
    organization {
      id
      billableRate
      brandsEnabled
      countryCode
      createdAt
      currencyCode
      customerDashboardEnabled
      defaultTaskVisibility
      domain
      jiraEnabled
      name
      pmCanEditSkills
      primaryColor
      resourceManagementEnabled
      secureLinksEnabled
      taskFilterByFunction
      timeTrackingEnabled
      trial
      type
      whiteLabelingEnabled
      adminSettings {
        uploadLimitMb
      }
      logo {
        originalUrl
      }
      setting {
        useStatusChangeReasons
      }
    }
  }
`
